import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { 
  Upload, FileSpreadsheet, AlertTriangle, CheckCircle, XCircle, 
  Clock, Download, RotateCcw, RefreshCw, Trash2, ChevronRight,
  FileWarning, ListChecks, History, Loader2, AlertCircle, Info,
  Calendar, Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/formatters';

const API = process.env.REACT_APP_BACKEND_URL;

const UploadsPage = () => {
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  
  // State
  const [sessions, setSessions] = useState([]);
  const [queues, setQueues] = useState({ missing_deal: 0, unmatched_bounce: 0, unmatched_refund: 0 });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState(null); // 'payments' | 'bounces'
  const [preflightResult, setPreflightResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [message, setMessage] = useState(null);
  const [paidOffDeals, setPaidOffDeals] = useState([]); // deals zeroed by the last upload
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [rollingBack, setRollingBack] = useState(false);
  const [skipDedup, setSkipDedup] = useState(true);
  
  // Date range filter state
  const [dateFilter, setDateFilter] = useState({
    mode: 'days', // 'days' | 'range'
    days: 30,
    startDate: '',
    endDate: '',
    uploadType: '', // '' | 'payments' | 'bounces'
    showFailed: false // Show failed/rolled_back attempts
  });
  const [showFilters, setShowFilters] = useState(false);

  const isAdmin = user?.roles?.includes('admin');

  const getHeaders = useCallback(() => ({
    ...getAuthHeaders(),
    'X-Tenant': 'live'  // Always use live tenant for uploads
  }), [getAuthHeaders]);

  const fetchSessions = useCallback(async () => {
    try {
      // Build query params based on filter mode
      const params = new URLSearchParams();
      
      if (dateFilter.mode === 'range' && (dateFilter.startDate || dateFilter.endDate)) {
        if (dateFilter.startDate) {
          params.append('start_date', dateFilter.startDate);
        }
        if (dateFilter.endDate) {
          params.append('end_date', dateFilter.endDate);
        }
      } else {
        params.append('days', dateFilter.days.toString());
      }
      
      if (dateFilter.uploadType) {
        params.append('upload_type', dateFilter.uploadType);
      }
      
      // Only show failed/rolled_back if explicitly requested
      if (dateFilter.showFailed) {
        params.append('include_failed', 'true');
      }
      
      const response = await axios.get(`${API}/api/uploads/sessions?${params.toString()}`, {
        headers: getHeaders()
      });
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  }, [getHeaders, dateFilter]);

  const fetchQueues = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/uploads/queues`, {
        headers: getHeaders()
      });
      setQueues(response.data.pending_counts || {});
    } catch (error) {
      console.error('Failed to fetch queues:', error);
    }
  }, [getHeaders]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchSessions(), fetchQueues()]);
    setLoading(false);
  }, [fetchSessions, fetchQueues]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFileSelect = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadType(type);
      setPreflightResult(null);
      setMessage(null);
    }
  };

  const runPreflight = async () => {
    if (!selectedFile || !uploadType) return;
    
    setUploading(true);
    setMessage(null);
    
    const formData = new FormData();
    formData.append('file', selectedFile);
    
    try {
      const qs = uploadType === 'payments' ? `?skip_dedup=${skipDedup}` : '';
      const response = await axios.post(
        `${API}/api/uploads/${uploadType}/preflight${qs}`,
        formData,
        { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      setPreflightResult(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Preflight check failed';
      setMessage({ type: 'error', text: detail });
      setPreflightResult(null);
    } finally {
      setUploading(false);
    }
  };

  const commitUpload = async () => {
    if (!selectedFile || !uploadType) return;
    
    setUploading(true);
    setMessage(null);
    
    const formData = new FormData();
    formData.append('file', selectedFile);
    
    try {
      // Use V2 endpoint for bounces (correct accounting: nullifies original payment)
      const baseEndpoint = uploadType === 'bounces' 
        ? `${API}/api/uploads/bounces/commit/v2`
        : `${API}/api/uploads/${uploadType}/commit`;
      const qs = uploadType === 'payments' ? `?skip_dedup=${skipDedup}` : '';
      const endpoint = baseEndpoint + qs;

      const response = await axios.post(
        endpoint,
        formData,
        { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      
      const result = response.data;
      
      // Handle V2 bounce response format
      if (uploadType === 'bounces' && result.summary) {
        const summary = result.summary;
        const achReturnsCount = summary.total_ach_returns || 0;
        const feesCount = summary.total_fees || 0;
        const unmatchedCount = summary.total_unmatched || 0;
        const ambiguousCount = summary.total_ambiguous || 0;
        const skippedCount = summary.total_skipped || 0;
        const missingDealsCount = summary.total_missing_deals || 0;
        const errorCount = summary.total_errors || 0;
        
        // Build detailed message
        let successMsg = `Bounce import complete: ${achReturnsCount} ACH returns, ${feesCount} fees created.`;
        
        // Build not-applied breakdown
        const notApplied = [];
        if (unmatchedCount > 0) notApplied.push(`${unmatchedCount} unmatched (queued)`);
        if (ambiguousCount > 0) notApplied.push(`${ambiguousCount} ambiguous (queued)`);
        if (skippedCount > 0) notApplied.push(`${skippedCount} duplicates skipped`);
        if (missingDealsCount > 0) notApplied.push(`${missingDealsCount} missing deals`);
        if (errorCount > 0) notApplied.push(`${errorCount} errors`);
        
        if (notApplied.length > 0) {
          successMsg += ` Not applied: ${notApplied.join(', ')}.`;
          toast.warning(successMsg);
        } else {
          toast.success(successMsg);
        }
        
        setMessage({ 
          type: notApplied.length > 0 ? 'warning' : 'success', 
          text: successMsg
        });
        
        // Store detailed results for potential download
        if (result.skipped_duplicates?.length > 0 || result.unmatched?.length > 0 || result.missing_deals?.length > 0) {
          console.log('Bounce import details:', {
            skipped: result.skipped_duplicates,
            unmatched: result.unmatched,
            ambiguous: result.ambiguous,
            missing_deals: result.missing_deals,
            errors: result.errors
          });
        }
      } else {
        toast.success(`Upload complete: ${result.imported || result.applied} records processed`);
        setMessage({
          type: 'success',
          text: `Successfully imported ${result.imported || result.applied} records. ${result.skipped || 0} skipped, ${result.errors || 0} errors.`
        });
      }

      // Paid-off banner: deals this upload pushed to a zero balance
      if (result.paid_off_deals?.length > 0) {
        setPaidOffDeals(result.paid_off_deals);
      }
      
      // Reset and refresh
      setSelectedFile(null);
      setUploadType(null);
      setPreflightResult(null);
      fetchData();
      
    } catch (error) {
      const detail = error.response?.data?.detail || 'Upload failed';
      setMessage({ type: 'error', text: detail });
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const handleRollback = async (sessionId) => {
    if (!window.confirm('This will revert all changes from this upload. Continue?')) return;
    
    setRollingBack(true);
    try {
      const response = await axios.post(
        `${API}/api/uploads/sessions/${sessionId}/rollback`,
        {},
        { headers: getHeaders() }
      );
      const result = response.data;
      toast.success(`Rollback completed: ${result.changes_reverted} transactions removed, ${result.affected_deals} deals updated`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rollback failed');
    } finally {
      setRollingBack(false);
    }
  };

  const handleDownload = async (session) => {
    setDownloading(session.id);
    try {
      const response = await fetch(
        `${API}/api/uploads/sessions/${session.id}/download`,
        { headers: getHeaders() }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Download failed');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', session.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (error) {
      toast.error(error.message || 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadAuditReport = async (session) => {
    try {
      const response = await fetch(
        `${API}/api/uploads/sessions/${session.id}/audit-report`,
        { headers: getHeaders() }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Audit report not available');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = (session.filename || 'import').replace(/\.csv$/i, '');
      link.setAttribute('download', `audit-report-${safeName}-${session.id.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Audit report downloaded');
    } catch (error) {
      toast.error(error.message || 'Download failed');
    }
  };

  const handleDownloadSkipped = async (sessionId) => {
    try {
      const response = await fetch(
        `${API}/api/uploads/sessions/${sessionId}/skipped/download`,
        { headers: getHeaders() }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Download failed');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `skipped_rows_${sessionId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Skipped rows downloaded');
    } catch (error) {
      toast.error(error.message || 'Download failed - no skipped data stored for this session');
    }
  };

  const handleExportQueue = async (queueType, label) => {
    try {
      const res = await axios.get(`${API}/api/uploads/queues`, {
        params: { queue_type: queueType, status: 'pending', limit: 500 },
        headers: getAuthHeaders(),
      });
      const items = res.data?.items || [];
      if (!items.length) { toast.info(`No pending items in ${label}`); return; }

      // Build CSV from all non-internal fields
      const FIELDS = ['queue_type', 'deal_id', 'merchant_name', 'amount', 'date', 'reference', 'return_code', 'created_at', 'session_id', 'status'];
      const header = FIELDS.join(',');
      const rows = items.map(item =>
        FIELDS.map(f => {
          const v = item[f] ?? '';
          return `"${String(v).replace(/"/g, '""')}"`;
        }).join(',')
      );
      const csv = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${queueType}_queue_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${items.length} items`);
    } catch {
      toast.error('Export failed');
    }
  };

  const handleApplyEligible = async (queueType) => {
    try {
      const response = await axios.post(
        `${API}/api/uploads/queues/apply-eligible?queue_type=${queueType}`,
        {},
        { headers: getHeaders() }
      );
      toast.success(`Applied ${response.data.applied} items from queue`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply queue items');
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const response = await axios.post(
        `${API}/api/uploads/admin/cleanup/reset-core-data?force=true`,
        {},
        { headers: getHeaders() }
      );
      toast.success('Core data reset completed');
      setShowResetConfirm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const cancelUpload = () => {
    setSelectedFile(null);
    setUploadType(null);
    setPreflightResult(null);
    setMessage(null);
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-100 text-green-800',
      completed_with_warnings: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      processing: 'bg-blue-100 text-blue-800',
      rolled_back: 'bg-gray-100 text-gray-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status?.replace(/_/g, ' ')}</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="uploads-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Daily Uploads</h1>
          <p className="text-slate-600">Upload payments and bounces to LIVE database - Power Funding Solutions</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
            LIVE Tenant Only
          </Badge>
          <Button variant="ghost" size="sm" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Paid-off banner (temporary — dismissible, cleared on next upload) */}
      {paidOffDeals.length > 0 && (
        <Alert className="border-emerald-300 bg-emerald-50" data-testid="paid-off-banner">
          <CheckCircle className="h-4 w-4 text-emerald-600" />
          <AlertDescription className="text-emerald-800">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                {paidOffDeals.map((d) => (
                  <div key={d.deal_uuid} className="font-medium">
                    🎉 {d.deal_name} has been paid off —{' '}
                    <a href={`/deals/${d.deal_uuid}`} className="underline font-semibold text-emerald-900">
                      #{d.deal_id_number}
                    </a>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setPaidOffDeals([])}
                className="text-emerald-700 hover:text-emerald-900 font-bold px-1"
                aria-label="Dismiss"
              >
                ✕
              </button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Message */}
      {message && (
        <Alert className={message.type === 'error' ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}>
          {message.type === 'error' ? <XCircle className="h-4 w-4 text-red-600" /> : <CheckCircle className="h-4 w-4 text-green-600" />}
          <AlertDescription className={message.type === 'error' ? 'text-red-700' : 'text-green-700'}>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      {/* CSV Templates Section */}
      <Card className="border-blue-200 bg-blue-50/30" data-testid="csv-templates-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Download className="h-5 w-5 text-blue-600" />
            CSV Templates
          </CardTitle>
          <CardDescription>
            Download sample templates to ensure your CSV files have the correct format
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            {/* Payments Template */}
            <div className="border border-blue-200 rounded-lg p-4 bg-white">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-medium text-slate-800 flex items-center gap-2">
                    <FileSpreadsheet className="h-4 w-4 text-green-600" />
                    Payments Template
                  </h4>
                  <p className="text-xs text-slate-500 mt-1">Daily ACH, Wire, Zelle payments</p>
                </div>
                <a
                  href={`${API}/api/uploads/templates/payments.csv`}
                  download="payments_template.csv"
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                  data-testid="download-payments-template"
                >
                  <Download className="h-3.5 w-3.5" />
                  Download
                </a>
              </div>
              <div className="text-xs space-y-1.5">
                <div>
                  <span className="font-medium text-slate-700">Required columns:</span>
                  <span className="text-slate-600 ml-1">Effective Date, Transfer Description, DR Amount, Reference Number</span>
                </div>
                <div className="text-slate-500">
                  <span className="font-medium">• Date:</span> YYYY-MM-DD or MM/DD/YYYY
                </div>
                <div className="text-slate-500">
                  <span className="font-medium">• Deal ID:</span> Numeric (10101) or alphanumeric (E201)
                </div>
                <div className="text-slate-500">
                  <span className="font-medium">• Reference:</span> Optional - auto-generated if blank
                </div>
              </div>
            </div>

            {/* Bounces Template */}
            <div className="border border-amber-200 rounded-lg p-4 bg-white">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-medium text-slate-800 flex items-center gap-2">
                    <FileWarning className="h-4 w-4 text-amber-600" />
                    Bounces Template
                  </h4>
                  <p className="text-xs text-slate-500 mt-1">ACH returns and bounce fees</p>
                </div>
                <a
                  href={`${API}/api/uploads/templates/bounces.csv`}
                  download="bounces_template.csv"
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-amber-100 text-amber-700 rounded-md hover:bg-amber-200 transition-colors"
                  data-testid="download-bounces-template"
                >
                  <Download className="h-3.5 w-3.5" />
                  Download
                </a>
              </div>
              <div className="text-xs space-y-1.5">
                <div>
                  <span className="font-medium text-slate-700">Required columns:</span>
                  <span className="text-slate-600 ml-1">Date, Merchant Number, Amount, Reference Number</span>
                </div>
                <div className="text-slate-500">
                  <span className="font-medium">• Reference Number:</span> Original payment's reference that bounced
                </div>
                <div className="text-slate-500">
                  <span className="font-medium">• Notes:</span> Add "R08" for $100 fee (default is $35)
                </div>
                <div className="text-slate-500">
                  <span className="font-medium">• Fees:</span> Auto-created: $35 default, $100 if Notes contains R08
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rollback Loading Dialog */}
      <Dialog open={rollingBack} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="relative">
              <Loader2 className="h-12 w-12 text-orange-500 animate-spin" />
              <RotateCcw className="h-6 w-6 text-orange-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold text-gray-900">Rollback in Progress</h3>
              <p className="text-sm text-gray-500">
                Reverting transactions and updating deal balances...
              </p>
              <p className="text-xs text-gray-400">
                Please do not close this window
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Payments Upload */}
        <Card data-testid="payments-upload-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-green-600" />
              Upload Payments CSV
            </CardTitle>
            <CardDescription>
              Daily payment collections (date, deal_id_number, amount, reference)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {uploadType !== 'payments' ? (
              <div>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => handleFileSelect(e, 'payments')}
                  className="hidden"
                  id="payments-file"
                  disabled={uploading}
                  data-testid="payments-file-input"
                />
                <label htmlFor="payments-file">
                  <Button asChild variant="outline" className="w-full cursor-pointer" disabled={uploading}>
                    <span>
                      <Upload className="h-4 w-4 mr-2" />
                      Select Payments CSV
                    </span>
                  </Button>
                </label>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <FileSpreadsheet className="h-4 w-4 text-slate-500" />
                  <span className="font-medium">{selectedFile?.name}</span>
                </div>

                <label className="flex items-start gap-2 text-sm p-3 rounded-md border border-amber-300 bg-amber-50 cursor-pointer select-none" data-testid="skip-dedup-label">
                  <input
                    type="checkbox"
                    checked={skipDedup}
                    onChange={(e) => setSkipDedup(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-amber-500 text-amber-600 focus:ring-amber-500"
                    data-testid="skip-dedup-checkbox"
                    disabled={uploading}
                  />
                  <span className="flex-1">
                    <span className="font-semibold text-amber-900">Skip duplicate check</span>
                    <span className={`block text-xs mt-0.5 ${skipDedup ? 'text-amber-800' : 'text-slate-600'}`}>
                      {skipDedup
                        ? 'Dedup is OFF — every row will import regardless of existing transactions.'
                        : 'Dedup is ON — rows matching (deal + date + amount + reference) will be skipped.'}
                    </span>
                  </span>
                </label>

                {!preflightResult && (
                  <div className="flex gap-2">
                    <Button onClick={runPreflight} disabled={uploading} className={`flex-1 ${skipDedup ? 'bg-amber-600 hover:bg-amber-700' : ''}`} data-testid="run-preflight-btn">
                      {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ListChecks className="h-4 w-4 mr-2" />}
                      Run Preflight{skipDedup ? ' (No Dedup)' : ''}
                    </Button>
                    <Button variant="outline" onClick={cancelUpload}>Cancel</Button>
                  </div>
                )}
                
                {preflightResult && (
                  <PreflightResults 
                    result={preflightResult} 
                    onCommit={commitUpload} 
                    onCancel={cancelUpload}
                    uploading={uploading}
                  />
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bounces Upload */}
        <Card data-testid="bounces-upload-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileWarning className="h-5 w-5 text-amber-600" />
              Upload Bounces CSV
            </CardTitle>
            <CardDescription>
              MCA Suite bounce report (Date, Merchant Number, Amount, Reference Number)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {uploadType !== 'bounces' ? (
              <div>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => handleFileSelect(e, 'bounces')}
                  className="hidden"
                  id="bounces-file"
                  disabled={uploading}
                  data-testid="bounces-file-input"
                />
                <label htmlFor="bounces-file">
                  <Button asChild variant="outline" className="w-full cursor-pointer" disabled={uploading}>
                    <span>
                      <Upload className="h-4 w-4 mr-2" />
                      Select Bounces CSV
                    </span>
                  </Button>
                </label>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <FileSpreadsheet className="h-4 w-4 text-slate-500" />
                  <span className="font-medium">{selectedFile?.name}</span>
                </div>
                
                {!preflightResult && (
                  <div className="flex gap-2">
                    <Button onClick={runPreflight} disabled={uploading} className="flex-1" data-testid="run-preflight-bounces-btn">
                      {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ListChecks className="h-4 w-4 mr-2" />}
                      Run Preflight Check
                    </Button>
                    <Button variant="outline" onClick={cancelUpload}>Cancel</Button>
                  </div>
                )}
                
                {preflightResult && (
                  <PreflightResults 
                    result={preflightResult} 
                    onCommit={commitUpload} 
                    onCancel={cancelUpload}
                    uploading={uploading}
                    isBounces={true}
                  />
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Fix Queues */}
      <Card data-testid="fix-queues-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            Fix Queues
          </CardTitle>
          <CardDescription>
            Items skipped during upload that may now be eligible for processing
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">Missing Deals</span>
                <Badge variant="outline" data-testid="missing-deal-count">{queues.missing_deal || 0}</Badge>
              </div>
              <p className="text-sm text-slate-600 mb-3">Payments for deals not yet in system</p>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => handleApplyEligible('missing_deal')}
                  disabled={!queues.missing_deal}
                  data-testid="apply-missing-deals-btn"
                  title="Apply pending queue items to matching deals"
                >
                  Apply Eligible
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleExportQueue('missing_deal', 'Missing Deals')}
                  disabled={!queues.missing_deal}
                  data-testid="export-missing-deals-btn"
                  className="gap-1"
                >
                  <Download className="h-3.5 w-3.5" />
                  Export
                </Button>
              </div>
            </div>
            
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">Unmatched Bounces</span>
                <Badge variant="outline" data-testid="unmatched-bounce-count">{queues.unmatched_bounce || 0}</Badge>
              </div>
              <p className="text-sm text-slate-600 mb-3">Bounces without matching payment</p>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => handleApplyEligible('unmatched_bounce')}
                  disabled={!queues.unmatched_bounce}
                  data-testid="apply-unmatched-bounces-btn"
                >
                  Apply Eligible
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleExportQueue('unmatched_bounce', 'Unmatched Bounces')}
                  disabled={!queues.unmatched_bounce}
                  data-testid="export-unmatched-bounces-btn"
                  className="gap-1"
                >
                  <Download className="h-3.5 w-3.5" />
                  Export
                </Button>
              </div>
            </div>
            
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">Unmatched Refunds</span>
                <Badge variant="outline">{queues.unmatched_refund || 0}</Badge>
              </div>
              <p className="text-sm text-slate-600 mb-3">Refunds pending payment link</p>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => navigate('/uploads/queues/refunds')}
                  disabled={!queues.unmatched_refund}
                >
                  Review
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleExportQueue('unmatched_refund', 'Unmatched Refunds')}
                  disabled={!queues.unmatched_refund}
                  data-testid="export-unmatched-refunds-btn"
                  className="gap-1"
                >
                  <Download className="h-3.5 w-3.5" />
                  Export
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Uploads */}
      <Card data-testid="recent-uploads-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5 text-slate-600" />
                Upload History
              </CardTitle>
              <CardDescription>
                {dateFilter.mode === 'range' && (dateFilter.startDate || dateFilter.endDate)
                  ? `${dateFilter.startDate || 'Start'} to ${dateFilter.endDate || 'Now'}`
                  : `Last ${dateFilter.days} days`
                } - Power Funding Solutions
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowFilters(!showFilters)}
                data-testid="toggle-filters-btn"
              >
                <Filter className="h-4 w-4 mr-1" />
                Filters
              </Button>
              <Button variant="ghost" size="sm" onClick={fetchData} data-testid="refresh-sessions-btn">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* Date Range Filters */}
          {showFilters && (
            <div className="mt-4 p-4 border rounded-lg bg-slate-50 space-y-4" data-testid="date-filters">
              <div className="flex flex-wrap items-end gap-4">
                {/* Filter Mode */}
                <div className="w-40">
                  <Label className="text-xs">Filter Mode</Label>
                  <Select 
                    value={dateFilter.mode} 
                    onValueChange={(v) => setDateFilter(prev => ({...prev, mode: v}))}
                  >
                    <SelectTrigger data-testid="filter-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="days">Last N Days</SelectItem>
                      <SelectItem value="range">Date Range</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {dateFilter.mode === 'days' ? (
                  <div className="w-32">
                    <Label className="text-xs">Days</Label>
                    <Select 
                      value={dateFilter.days.toString()} 
                      onValueChange={(v) => setDateFilter(prev => ({...prev, days: parseInt(v)}))}
                    >
                      <SelectTrigger data-testid="days-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7">7 days</SelectItem>
                        <SelectItem value="14">14 days</SelectItem>
                        <SelectItem value="30">30 days</SelectItem>
                        <SelectItem value="60">60 days</SelectItem>
                        <SelectItem value="90">90 days</SelectItem>
                        <SelectItem value="180">180 days</SelectItem>
                        <SelectItem value="365">365 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <>
                    <div className="w-40">
                      <Label className="text-xs">Start Date</Label>
                      <Input 
                        type="date" 
                        value={dateFilter.startDate}
                        onChange={(e) => setDateFilter(prev => ({...prev, startDate: e.target.value}))}
                        data-testid="start-date-input"
                      />
                    </div>
                    <div className="w-40">
                      <Label className="text-xs">End Date</Label>
                      <Input 
                        type="date" 
                        value={dateFilter.endDate}
                        onChange={(e) => setDateFilter(prev => ({...prev, endDate: e.target.value}))}
                        data-testid="end-date-input"
                      />
                    </div>
                  </>
                )}
                
                {/* Upload Type Filter */}
                <div className="w-36">
                  <Label className="text-xs">Type</Label>
                  <Select 
                    value={dateFilter.uploadType || 'all'} 
                    onValueChange={(v) => setDateFilter(prev => ({...prev, uploadType: v === 'all' ? '' : v}))}
                  >
                    <SelectTrigger data-testid="upload-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="payments">Payments</SelectItem>
                      <SelectItem value="bounces">Bounces</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Show Failed Toggle */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="show-failed"
                    checked={dateFilter.showFailed}
                    onChange={(e) => setDateFilter(prev => ({...prev, showFailed: e.target.checked}))}
                    className="h-4 w-4 rounded border-gray-300"
                    data-testid="show-failed-checkbox"
                  />
                  <Label htmlFor="show-failed" className="text-xs cursor-pointer">Show failed</Label>
                </div>
                
                {/* Apply Button */}
                <Button size="sm" onClick={fetchSessions} data-testid="apply-filters-btn">
                  Apply
                </Button>
                
                {/* Clear Filters */}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setDateFilter({ mode: 'days', days: 30, startDate: '', endDate: '', uploadType: '', showFailed: false })}
                  data-testid="clear-filters-btn"
                >
                  Clear
                </Button>
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {sessions.length === 0 ? (
            <p className="text-slate-500 text-center py-4">No uploads yet</p>
          ) : (
            <div className="space-y-2">
              {sessions.map((session) => (
                <div key={session.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50" data-testid={`session-${session.id}`}>
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="h-4 w-4 text-slate-400" />
                    <div>
                      <div className="font-medium text-sm">{session.filename}</div>
                      <div className="text-xs text-slate-500">
                        {session.upload_type} • {new Date(session.uploaded_at).toLocaleString()} • by {session.uploaded_by_email}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(session.status)}
                    <span className="text-sm text-slate-600">
                      {session.counts?.imported || session.counts?.applied || session.counts?.applied_bounces || session.counts?.total_ach_returns || 0} imported
                      {session.upload_type === 'bounces' && (session.counts?.applied_fees > 0 || session.counts?.total_fees > 0) && (
                        <span className="text-slate-500 ml-1">(+{session.counts.applied_fees || session.counts.total_fees} fees)</span>
                      )}
                      {(session.counts?.skipped > 0 || session.counts?.total_skipped > 0) && (
                        <span className="text-amber-600 ml-1">({session.counts.skipped || session.counts.total_skipped} skipped)</span>
                      )}
                      {(session.counts?.total_unmatched > 0 || session.counts?.total_ambiguous > 0) && (
                        <span className="text-blue-600 ml-1">({(session.counts.total_unmatched || 0) + (session.counts.total_ambiguous || 0)} queued)</span>
                      )}
                      {(session.counts?.total_errors > 0) && (
                        <span className="text-red-600 ml-1">({session.counts.total_errors} errors)</span>
                      )}
                    </span>
                    {/* Download Original CSV Button — only if file stored */}
                    {session.file_path && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDownload(session)}
                      disabled={downloading === session.id}
                      title="Download original CSV"
                      data-testid={`download-btn-${session.id}`}
                    >
                      {downloading === session.id ? (
                        <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                      ) : (
                        <Download className="h-4 w-4 text-blue-600" />
                      )}
                    </Button>
                    )}
                    {/* Download Skipped Rows Button */}
                    {(session.counts?.skipped > 0 || session.counts?.total_skipped > 0 || session.counts?.total_unmatched > 0 || session.counts?.total_ambiguous > 0 || session.counts?.errors > 0 || session.counts?.total_errors > 0) && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDownloadSkipped(session.id)}
                        title="Download skipped/queued/error rows"
                        data-testid={`download-skipped-btn-${session.id}`}
                      >
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                      </Button>
                    )}
                    {/* Download Audit Report PDF — only for payments uploads that ran with audit tracking */}
                    {session.upload_type === 'payments' && session.has_audit_report && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownloadAuditReport(session)}
                        title="Download Import Audit Report (PDF)"
                        data-testid={`audit-report-btn-${session.id}`}
                      >
                        <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
                      </Button>
                    )}
                    {session.status !== 'rolled_back' && isAdmin && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleRollback(session.id)}
                        title="Rollback this upload"
                        data-testid={`rollback-btn-${session.id}`}
                      >
                        <RotateCcw className="h-4 w-4 text-amber-600" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Smart ellipsis — load older sessions */}
          {sessions.length > 0 && dateFilter.mode === 'days' && (() => {
            const STEPS = [30, 60, 90, 180, 365];
            const currentIdx = STEPS.indexOf(dateFilter.days);
            const nextDays = currentIdx >= 0 && currentIdx < STEPS.length - 1 ? STEPS[currentIdx + 1] : null;
            return (
              <div className="mt-3 flex items-center gap-2 text-xs text-slate-400">
                <div className="flex-1 border-t border-dashed border-slate-200" />
                <span className="shrink-0">
                  Showing {sessions.length} session{sessions.length !== 1 ? 's' : ''} — last {dateFilter.days} days
                </span>
                <div className="flex-1 border-t border-dashed border-slate-200" />
                {nextDays && (
                  <button
                    onClick={() => {
                      setDateFilter(prev => ({ ...prev, days: nextDays }));
                    }}
                    className="shrink-0 px-2 py-1 rounded border border-slate-200 hover:border-slate-400 hover:text-slate-600 transition-colors whitespace-nowrap"
                    data-testid="load-older-btn"
                  >
                    ···  Load older ({nextDays}d)
                  </button>
                )}
                {!nextDays && (
                  <span className="shrink-0 text-slate-300 italic">full history shown</span>
                )}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Admin: Reset Data */}
      {isAdmin && (
        <Card className="border-red-200" data-testid="reset-data-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" />
              Reset Deals Data (Keep Users/Contacts)
            </CardTitle>
            <CardDescription>
              One-time cleanup: Delete all deals, transactions, syndications, withdrawals from Power Funding Solutions. Keeps users and contacts.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!showResetConfirm ? (
              <Button 
                variant="destructive" 
                onClick={() => setShowResetConfirm(true)}
                data-testid="reset-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Reset Core Data
              </Button>
            ) : (
              <Alert className="border-red-300 bg-red-50">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-700">
                  <p className="font-semibold mb-2">⚠️ DESTRUCTIVE ACTION</p>
                  <p className="text-sm mb-3">
                    This will permanently delete ALL deals, transactions, syndications, and withdrawals from BOTH live and validation tenants.
                    Users and contacts will be preserved.
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={handleReset}
                      disabled={resetting}
                      data-testid="confirm-reset-btn"
                    >
                      {resetting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                      Confirm Reset
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowResetConfirm(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </div>
    </Layout>
  );
};

// Preflight Results Component
const PreflightResults = ({ result, onCommit, onCancel, uploading, isBounces = false }) => {
  const hasBlockingErrors = result.blocking_errors?.length > 0;
  const hasDuplicates = result.skipped_duplicates?.length > 0 || result.duplicates_skipped > 0;
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [showMissingDeals, setShowMissingDeals] = useState(false);
  const [showUnmatchedBounces, setShowUnmatchedBounces] = useState(false);
  const [showAlreadyBounced, setShowAlreadyBounced] = useState(false);
  const [showImportPreview, setShowImportPreview] = useState(false);
  
  // For bounces, calculate why rows are blocked/skipped
  const unmatchedCount = result.unmatched_bounces_count || result.unmatched_bounces?.length || 0;
  const alreadyBouncedCount = result.already_bounced_count || result.already_bounced?.length || 0;
  
  // Count auto-generated references in preview
  const previewData = isBounces ? result.bounces_preview : result.preview;
  const autoGeneratedCount = previewData?.filter(row => row.is_generated_ref)?.length || 0;
  const hasAutoGeneratedRefs = autoGeneratedCount > 0;
  
  return (
    <div className="space-y-3 border rounded-lg p-3 bg-slate-50" data-testid="preflight-results">
      <div className="flex items-center justify-between">
        <span className="font-medium">Preflight Results</span>
        {result.can_proceed ? (
          <Badge className="bg-green-100 text-green-800">Ready to Import</Badge>
        ) : (
          <Badge className="bg-red-100 text-red-800">Blocked</Badge>
        )}
      </div>
      
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>Total Rows: <span className="font-medium">{result.total_rows}</span></div>
        {result.mca_format_detected && (
          <div className="col-span-2 mb-1">
            <Badge className="bg-blue-100 text-blue-800" data-testid="mca-format-badge">MCA Suite Format Detected</Badge>
            {result.mca_meta?.type_mapping && (
              <div className="flex flex-wrap gap-1 mt-1">
                {Object.entries(result.mca_meta.type_mapping).map(([t, c]) => (
                  <Badge key={t} variant="outline" className="text-xs">{t}: {c}</Badge>
                ))}
              </div>
            )}
          </div>
        )}
        <div>
          {isBounces ? 'Bounces to Apply' : 'Rows to Import'}: 
          <span className="font-medium text-green-600 ml-1">{isBounces ? result.bounces_to_apply : result.rows_to_import}</span>
        </div>
        {result.missing_deals_count > 0 && (
          <div className="text-amber-600">
            Missing Deals: <span className="font-medium">{result.missing_deals_count}</span> (will queue)
          </div>
        )}
        {unmatchedCount > 0 && (
          <div className="text-orange-600">
            No Matching Payment: <span className="font-medium">{unmatchedCount}</span>
          </div>
        )}
        {alreadyBouncedCount > 0 && (
          <div className="text-blue-600">
            Already Processed: <span className="font-medium">{alreadyBouncedCount}</span>
          </div>
        )}
        {(result.duplicates_skipped > 0 || result.skipped_duplicates_count > 0) && (
          <div className="text-orange-600">
            Duplicates Skipped: <span className="font-medium">{result.duplicates_skipped || result.skipped_duplicates_count}</span>
          </div>
        )}
      </div>
      
      {/* Breakdown Summary for Bounces */}
      {isBounces && result.breakdown && (
        <div className="border border-slate-200 rounded p-2 bg-white">
          <p className="text-slate-700 font-medium text-sm mb-2">Row Breakdown:</p>
          <div className="grid grid-cols-2 gap-1 text-xs">
            <div className="text-green-600">✓ Bounces to Apply: {result.breakdown.bounces_to_apply || result.breakdown.to_apply || 0}</div>
            <div className="text-green-600">✓ Fees to Import: {result.breakdown.fees_to_import || 0}</div>
            {result.breakdown.unmatched_bounces > 0 && (
              <div className="text-blue-600">ℹ No Matching Payment: {result.breakdown.unmatched_bounces} (will import)</div>
            )}
            <div className="text-slate-500">↷ Non-Bounce Rows: {result.breakdown.positive_rows || 0}</div>
            {result.breakdown.missing_deals > 0 && (
              <div className="text-amber-600">⏳ Missing Deals: {result.breakdown.missing_deals}</div>
            )}
            {result.breakdown.already_bounced > 0 && (
              <div className="text-blue-600">✓ Already Processed: {result.breakdown.already_bounced}</div>
            )}
            {result.breakdown.errors > 0 && (
              <div className="text-red-600">✗ Parse Errors: {result.breakdown.errors}</div>
            )}
          </div>
        </div>
      )}
      
      {/* Import Preview Section - Shows reference generation details */}
      {previewData?.length > 0 && (
        <div className="border border-green-200 rounded p-2 bg-green-50" data-testid="import-preview-section">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setShowImportPreview(!showImportPreview)}
          >
            <div className="flex items-center gap-2">
              <p className="text-green-700 font-medium text-sm flex items-center gap-1">
                <CheckCircle className="h-4 w-4" />
                Import Preview ({previewData.length} sample rows)
              </p>
              {hasAutoGeneratedRefs && (
                <Badge className="bg-purple-100 text-purple-800 text-xs" data-testid="auto-generated-badge">
                  {autoGeneratedCount} Auto-Generated Ref{autoGeneratedCount !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-green-700">
              {showImportPreview ? 'Hide' : 'Show Details'}
            </Button>
          </div>
          {hasAutoGeneratedRefs && !showImportPreview && (
            <p className="text-xs text-purple-600 mt-1">
              ⚡ Some references will be auto-generated for generic sources (Zelle, Venmo, blank, etc.)
            </p>
          )}
          {showImportPreview && (
            <div className="mt-2 max-h-64 overflow-y-auto border-t border-green-200 pt-2">
              <table className="w-full text-xs">
                <thead className="text-green-800 sticky top-0 bg-green-50">
                  <tr>
                    <th className="text-left py-1">Row</th>
                    <th className="text-left py-1">Deal</th>
                    <th className="text-left py-1">Date</th>
                    <th className="text-right py-1">Amount</th>
                    <th className="text-left py-1">Original Ref</th>
                    <th className="text-left py-1">Final Reference</th>
                    <th className="text-left py-1">Status</th>
                  </tr>
                </thead>
                <tbody className="text-green-700">
                  {previewData.map((row, i) => (
                    <tr key={i} className="border-t border-green-100" data-testid={`preview-row-${i}`}>
                      <td className="py-1">{row.row}</td>
                      <td className="py-1 font-mono">{row.deal_id_number}</td>
                      <td className="py-1">{row.date}</td>
                      <td className="py-1 text-right">{formatCurrency(row.amount || row.payment_amount)}</td>
                      <td className="py-1 font-mono text-slate-600 max-w-24 truncate" title={row.reference_original || '-'}>
                        {row.reference_original || <span className="text-slate-400 italic">blank</span>}
                      </td>
                      <td className="py-1 font-mono max-w-32 truncate" title={row.reference || row.bounce_reference}>
                        {row.reference || row.bounce_reference}
                      </td>
                      <td className="py-1">
                        {row.is_generated_ref ? (
                          <div className="flex flex-col gap-0.5">
                            <Badge className="bg-purple-100 text-purple-700 text-[10px] px-1 py-0" data-testid={`auto-gen-badge-${i}`}>
                              Auto-generated
                            </Badge>
                            {row.payment_source && (
                              <span className="text-[10px] text-purple-600">
                                {row.reference_original ? `Generic: ${row.payment_source}` : 'Blank reference'}
                              </span>
                            )}
                          </div>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600 text-[10px] px-1 py-0">
                            Preserved
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(result.rows_to_import || result.bounces_to_apply) > previewData.length && (
                <div className="text-xs text-green-500 mt-1 pt-1 border-t border-green-100">
                  Showing first {previewData.length} of {result.rows_to_import || result.bounces_to_apply} rows to import
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Duplicates Alert Section */}
      {hasDuplicates && (
        <div className="border border-orange-200 rounded p-2 bg-orange-50">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setShowDuplicates(!showDuplicates)}
          >
            <p className="text-orange-700 font-medium text-sm flex items-center gap-1">
              <AlertTriangle className="h-4 w-4" />
              {result.duplicates_skipped || result.skipped_duplicates_count} Duplicate(s) Skipped
            </p>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-orange-700">
              {showDuplicates ? 'Hide' : 'Show Details'}
            </Button>
          </div>
          {showDuplicates && result.skipped_duplicates && (
            <div className="mt-2 max-h-48 overflow-y-auto border-t border-orange-200 pt-2">
              <table className="w-full text-xs">
                <thead className="text-orange-800">
                  <tr>
                    <th className="text-left py-1">Row</th>
                    <th className="text-left py-1">Deal</th>
                    <th className="text-left py-1">Reference</th>
                    <th className="text-left py-1">Reason</th>
                  </tr>
                </thead>
                <tbody className="text-orange-700">
                  {result.skipped_duplicates.slice(0, 20).map((dup, i) => (
                    <tr key={i} className="border-t border-orange-100">
                      <td className="py-1">{dup.row}</td>
                      <td className="py-1">{dup.deal_id}</td>
                      <td className="py-1 font-mono text-xs max-w-32 truncate" title={dup.reference}>
                        {dup.reference_original || dup.reference}
                      </td>
                      <td className="py-1">{dup.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {result.skipped_duplicates.length > 20 && (
                <div className="text-xs text-orange-500 mt-1 pt-1 border-t border-orange-100">
                  ...and {result.skipped_duplicates.length - 20} more duplicates
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Missing Deals Alert Section */}
      {result.missing_deals_count > 0 && result.missing_deals?.length > 0 && (
        <div className="border border-amber-200 rounded p-2 bg-amber-50">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setShowMissingDeals(!showMissingDeals)}
          >
            <p className="text-amber-700 font-medium text-sm flex items-center gap-1">
              <AlertTriangle className="h-4 w-4" />
              {result.missing_deals_count} Missing Deal(s) - Will be queued
            </p>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-amber-700">
              {showMissingDeals ? 'Hide' : 'Show Details'}
            </Button>
          </div>
          {showMissingDeals && (
            <div className="mt-2 max-h-48 overflow-y-auto border-t border-amber-200 pt-2">
              <table className="w-full text-xs">
                <thead className="text-amber-800">
                  <tr>
                    <th className="text-left py-1">Row</th>
                    <th className="text-left py-1">Deal ID</th>
                    <th className="text-left py-1">Date</th>
                    <th className="text-right py-1">Amount</th>
                  </tr>
                </thead>
                <tbody className="text-amber-700">
                  {result.missing_deals.slice(0, 20).map((item, i) => (
                    <tr key={i} className="border-t border-amber-100">
                      <td className="py-1">{item.row}</td>
                      <td className="py-1 font-mono">{item.deal_id_number}</td>
                      <td className="py-1">{item.date}</td>
                      <td className="py-1 text-right">{formatCurrency(item.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {result.missing_deals.length > 20 && (
                <div className="text-xs text-amber-500 mt-1 pt-1 border-t border-amber-100">
                  ...and {result.missing_deals.length - 20} more missing deals
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Unmatched Bounces Info Section (Bounce-specific) - These WILL be imported */}
      {unmatchedCount > 0 && result.unmatched_bounces?.length > 0 && (
        <div className="border border-blue-200 rounded p-2 bg-blue-50">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setShowUnmatchedBounces(!showUnmatchedBounces)}
          >
            <p className="text-blue-700 font-medium text-sm flex items-center gap-1">
              <CheckCircle className="h-4 w-4" />
              {unmatchedCount} Bounce(s) Without Matching Payment - Will Import
            </p>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-blue-700">
              {showUnmatchedBounces ? 'Hide' : 'Show Details'}
            </Button>
          </div>
          <p className="text-xs text-blue-600 mt-1">These bounces will be imported as standalone records (no payment to mark as bounced)</p>
          {showUnmatchedBounces && (
            <div className="mt-2 max-h-48 overflow-y-auto border-t border-blue-200 pt-2">
              <table className="w-full text-xs">
                <thead className="text-blue-800">
                  <tr>
                    <th className="text-left py-1">Row</th>
                    <th className="text-left py-1">Deal ID</th>
                    <th className="text-left py-1">Date</th>
                    <th className="text-right py-1">Amount</th>
                    <th className="text-left py-1">Code</th>
                  </tr>
                </thead>
                <tbody className="text-blue-700">
                  {result.unmatched_bounces.slice(0, 20).map((item, i) => (
                    <tr key={i} className="border-t border-blue-100">
                      <td className="py-1">{item.row}</td>
                      <td className="py-1 font-mono">{item.deal_id_number}</td>
                      <td className="py-1">{item.date}</td>
                      <td className="py-1 text-right">{formatCurrency(item.amount)}</td>
                      <td className="py-1">{item.return_code || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {result.unmatched_bounces.length > 20 && (
                <div className="text-xs text-blue-500 mt-1 pt-1 border-t border-blue-100">
                  ...and {unmatchedCount - 20} more
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Already Bounced Alert Section (Bounce-specific) */}
      {alreadyBouncedCount > 0 && result.already_bounced?.length > 0 && (
        <div className="border border-blue-200 rounded p-2 bg-blue-50">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setShowAlreadyBounced(!showAlreadyBounced)}
          >
            <p className="text-blue-700 font-medium text-sm flex items-center gap-1">
              <CheckCircle className="h-4 w-4" />
              {alreadyBouncedCount} Already Processed (Idempotent Skip)
            </p>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-blue-700">
              {showAlreadyBounced ? 'Hide' : 'Show Details'}
            </Button>
          </div>
          {showAlreadyBounced && (
            <div className="mt-2 max-h-48 overflow-y-auto border-t border-blue-200 pt-2">
              <table className="w-full text-xs">
                <thead className="text-blue-800">
                  <tr>
                    <th className="text-left py-1">Row</th>
                    <th className="text-left py-1">Deal ID</th>
                    <th className="text-right py-1">Amount</th>
                    <th className="text-left py-1">Reason</th>
                  </tr>
                </thead>
                <tbody className="text-blue-700">
                  {result.already_bounced.slice(0, 20).map((item, i) => (
                    <tr key={i} className="border-t border-blue-100">
                      <td className="py-1">{item.row}</td>
                      <td className="py-1 font-mono">{item.deal_id_number}</td>
                      <td className="py-1 text-right">{formatCurrency(item.amount)}</td>
                      <td className="py-1 text-xs">{item.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {result.already_bounced.length > 20 && (
                <div className="text-xs text-blue-500 mt-1 pt-1 border-t border-blue-100">
                  ...and {alreadyBouncedCount - 20} more already processed
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {hasBlockingErrors && (
        <div className="border border-red-200 rounded p-2 bg-red-50 max-h-40 overflow-y-auto">
          <p className="text-red-700 font-medium text-sm mb-1">Blocking Errors:</p>
          {result.blocking_errors.slice(0, 10).map((err, i) => (
            <div key={i} className="text-xs text-red-600">
              Row {err.row}: {err.error}
            </div>
          ))}
          {result.blocking_errors.length > 10 && (
            <div className="text-xs text-red-500 mt-1">
              ...and {result.blocking_errors.length - 10} more errors
            </div>
          )}
        </div>
      )}
      
      <div className="flex gap-2">
        <Button 
          onClick={onCommit} 
          disabled={!result.can_proceed || uploading}
          className="flex-1"
          data-testid="commit-upload-btn"
        >
          {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
          Commit to LIVE
        </Button>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

export default UploadsPage;
